import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	ChartPieIcon,
	ChartBarIcon,
	AcademicCapIcon,
	ClipboardDocumentListIcon,
	SparklesIcon,
	ChevronDownIcon,
	ChevronRightIcon
} from '@heroicons/react/24/outline';
import {
	PieChart,
	Pie,
	Cell,
	ResponsiveContainer,
	Legend,
	Tooltip,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid
} from 'recharts';
import { useAdminContext } from '../../../api/AdminContext';
import { useQuestionBankContext } from '../../../api/QuestionBankContext';
import SelectElement from '../../../components/forms/SelectElement';
import Alert from '../../../components/ui/Alert';
import { testLabelOptions } from '../../../data/option-objects/examOptions';
import MainSection from '../../../layout/app/dashboard/MainSection';
import type {
	Question,
	Category,
	SubCategory
} from '../../../data/interface-question';
import type { QuestionBankID } from '../../../data/interface-question-bank';

// Extends project's Category interface for breakdown analysis
interface CategoryBreakdownData {
	id: string;
	name: string; // Using string instead of CategoryName for flexibility
	count: number;
	subCategoryList: SubCategoryBreakdown[];
}

// Extends project's SubCategory interface for breakdown analysis
interface SubCategoryBreakdown {
	name: string;
	count: number;
	specifications?: string; // From project's SubCategory interface
}

interface Tab {
	id: QuestionBankID;
	name: string;
	href: string;
	current: boolean;
}

interface ChartData {
	name: string;
	value: number;
	color: string;
}

// Enhanced color palette with modern gradients and accessibility
const CHART_COLORS = [
	'#6366f1', // brand-500 - Primary brand color
	'#8b5cf6', // violet-500
	'#06b6d4', // cyan-500
	'#10b981', // emerald-500
	'#f59e0b', // amber-500
	'#ef4444', // red-500
	'#ec4899', // pink-500
	'#84cc16', // lime-500
	'#f97316', // orange-500
	'#3b82f6', // blue-500
	'#0ea5e9', // sky-500
	'#8b5cf6' // purple-500
];

// Gradient definitions for enhanced visuals
const GRADIENT_COLORS = [
	'from-brand-500 to-purple-600',
	'from-violet-500 to-pink-500',
	'from-cyan-500 to-blue-500',
	'from-emerald-500 to-teal-500',
	'from-amber-500 to-orange-500',
	'from-red-500 to-pink-500',
	'from-pink-500 to-rose-500',
	'from-lime-500 to-green-500',
	'from-orange-500 to-red-500',
	'from-blue-500 to-brand-500',
	'from-sky-500 to-cyan-500',
	'from-purple-500 to-violet-500'
];

/**
 * CategoryBreakdown Component
 * Enterprise-grade admin report with modern UI design and enhanced visuals.
 * Features glassmorphism effects, progress bars, gradient cards, and micro-interactions.

 * Enhanced Features:
 * - Modern glassmorphism design with gradient backgrounds
 * - Interactive pie chart with hover animations and custom tooltips  
 * - Progress bars for visual category comparison
 * - Enhanced loading states with skeleton animations
 * - Beautiful empty states with illustrations
 * - Micro-interactions and hover effects
 * - Responsive design with mobile-optimized layouts
 * - Dark mode compatible color scheme

 * Architecture follows cursor.rules with performance optimizations and accessibility.
 * @returns JSX.Element - Enhanced category breakdown analysis interface
 */
const CategoryBreakdown: React.FC = () => {
	// Using admin context to get questions following Dashboard.tsx pattern
	const { adminQuestions, setAdminQuestions, getQuestionsForAdmin } =
		useAdminContext();

	// Using question bank context to get categories with proper names
	const { categories, getCategories } = useQuestionBankContext();

	// State management with proper typing
	const [questions, setQuestions] = useState<Question[]>([]);
	const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
	const [categoryData, setCategoryData] = useState<CategoryBreakdownData[]>([]);
	const [chartData, setChartData] = useState<ChartData[]>([]);
	const [selectedExam, setSelectedExam] = useState<string>('');
	const [bankID, setBankID] = useState<QuestionBankID>('enurm');
	const [showBreakdown, setShowBreakdown] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<string | null>(null);

	// Tabs configuration
	const [tabs, setTabs] = useState<Tab[]>([
		{ id: 'enurm', name: 'ENURM', href: '#', current: true },
		{ id: 'maxilofacial', name: 'ENURM-CMF', href: '#', current: false },
		{ id: 'dermatología', name: 'PrepDerm', href: '#', current: false }
	]);

	// Add view toggle state for chart/table switching
	const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');

	// State for tracking expanded/collapsed categories
	const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
		new Set()
	);

	// Utility function for className composition
	const classNames = useCallback((...classes: string[]): string => {
		return classes.filter(Boolean).join(' ');
	}, []);

	// Toggle category expansion
	const toggleCategoryExpansion = useCallback((categoryId: string): void => {
		setExpandedCategories((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(categoryId)) {
				newSet.delete(categoryId);
			} else {
				newSet.add(categoryId);
			}
			return newSet;
		});
	}, []);

	// Expand all categories
	const expandAllCategories = useCallback((): void => {
		const allCategoryIds = categoryData.map((category) => category.id);
		setExpandedCategories(new Set(allCategoryIds));
	}, [categoryData]);

	// Collapse all categories
	const collapseAllCategories = useCallback((): void => {
		setExpandedCategories(new Set());
	}, []);

	// Load questions following Dashboard.tsx pattern
	const loadQuestions = useCallback((): void => {
		setLoading(true);
		getQuestionsForAdmin(bankID)
			.then((questionList: Question[]) => {
				if (process.env.NODE_ENV === 'development') {
					console.log(`Loaded ${questionList.length} questions for ${bankID}`);
				}
				setQuestions(questionList);
				setLoading(false);
			})
			.catch((error: unknown) => {
				console.error('Error fetching questions:', error);
				setLoading(false);
			});
	}, [bankID]); // Only depend on bankID, not getQuestionsForAdmin to prevent infinite loop

	// Load categories on component mount and when bank changes
	useEffect(() => {
		// Always fetch categories when bank changes to ensure we have categories for the current bank
		getCategories().catch((error: unknown) => {
			console.error('Error fetching categories:', error);
		});
	}, [bankID]); // Only depend on bankID, not getCategories to prevent infinite loop

	// Fetch questions on component mount or bank change - following Dashboard.tsx logic
	useEffect(() => {
		if (adminQuestions === undefined || adminQuestions.length === 0) {
			loadQuestions();
		} else {
			setQuestions(adminQuestions);
		}
	}, [bankID, adminQuestions]); // Remove loadQuestions from deps to prevent infinite loop

	// Handle question bank change following Dashboard.tsx pattern
	const handleQBankChange = useCallback(
		(newBankID: QuestionBankID): void => {
			setBankID(newBankID);
			setAdminQuestions(undefined);
			setFilteredQuestions([]);
			setCategoryData([]);
			setChartData([]);
			setShowBreakdown(false);
			setSelectedExam(''); // Reset exam selection to trigger dropdown reset
			setExpandedCategories(new Set()); // Reset expanded categories
		},
		[setAdminQuestions]
	);

	// Process categories and subcategories with counts using actual category data
	const processCategories = useCallback(
		(questionsToProcess: Question[]): void => {
			const categoryMap = new Map<string, Question[]>();

			// Group questions by categoryID
			questionsToProcess.forEach((question: Question) => {
				const category = question.categoryID;
				if (!categoryMap.has(category)) {
					categoryMap.set(category, []);
				}
				const categoryQuestions = categoryMap.get(category);
				if (categoryQuestions !== undefined) {
					categoryQuestions.push(question);
				}
			});

			// Process each category using actual category data for proper names
			const processedCategories: CategoryBreakdownData[] = Array.from(
				categoryMap.entries()
			).map(([categoryID, categoryQuestions]) => {
				// Find the actual category data to get proper name (filter by current bank)
				const actualCategory = categories?.find(
					(cat: Category) =>
						cat.id === categoryID && cat.questionBank === bankID
				);
				const categoryName = actualCategory?.name ?? categoryID; // Fallback to ID if not found

				const subCategoryMap = new Map<string, number>();

				// Count subcategories from questions
				categoryQuestions.forEach((question: Question) => {
					const subCategory = question.subCategoryList ?? 'No Subcategory';
					subCategoryMap.set(
						subCategory,
						(subCategoryMap.get(subCategory) ?? 0) + 1
					);
				});

				// Convert to array format with proper subcategory data
				const subCategoryList: SubCategoryBreakdown[] = Array.from(
					subCategoryMap.entries()
				).map(([subCatName, count]) => {
					// Try to find actual subcategory data for specifications
					const actualSubCategory = actualCategory?.subCategoryList?.find(
						(sub: SubCategory) => sub.name === subCatName
					);

					return {
						name: subCatName,
						count,
						specifications: actualSubCategory?.specifications
					};
				});

				return {
					id: categoryID,
					name: categoryName, // CategoryName is already string type
					count: categoryQuestions.length,
					subCategoryList
				};
			});

			setCategoryData(processedCategories);

			// Prepare chart data using proper category names
			const preparedChartData: ChartData[] = processedCategories.map(
				(category, index) => ({
					name: category.name,
					value: category.count,
					color: CHART_COLORS[index % CHART_COLORS.length]
				})
			);
			setChartData(preparedChartData);
		},
		[]
	); // Remove dependencies to prevent infinite loop - we'll pass current values directly

	// Handle exam selection and filter questions
	const handleExamChange = useCallback(
		(examValue: string): void => {
			setSelectedExam(examValue);

			if (examValue.length === 0 || examValue === 'default') {
				setFilteredQuestions([]);
				setCategoryData([]);
				setChartData([]);
				setShowBreakdown(false);
				return;
			}

			const filtered = questions.filter(
				(question: Question) => question.source === examValue
			);

			if (filtered.length === 0 && questions.length > 0) {
				const sampleSources = questions
					.slice(0, 3)
					.map((q) => q.source)
					.join(', ');
				setAlertMessage(
					`No questions found for "${examValue}". Sample sources: ${sampleSources}`
				);
			} else {
				setAlertMessage(null);
				console.log(`Found ${filtered.length} questions for ${examValue}`);
			}

			setFilteredQuestions(filtered);
			processCategories(filtered);
			setShowBreakdown(true);
		},
		[questions]
	); // Remove processCategories and bankID to prevent infinite loop

	// Handle tab change
	const handleTabChange = useCallback((tabID: QuestionBankID): void => {
		handleQBankChange(tabID);

		const newTabs = tabs.map((tab: Tab) => ({
			...tab,
			current: tab.id === tabID
		}));

		setTabs(newTabs);
	}, []); // Remove dependencies to prevent infinite loop

	// Dynamic exam options based on selected question bank
	const examOptions = useMemo(() => {
		let options: Array<{ id: string; value: string }> = [];

		switch (bankID) {
			case 'enurm':
				options = testLabelOptions.enurmAdmin;
				break;
			case 'maxilofacial':
				options = testLabelOptions.maxilofacialAdmin;
				break;
			case 'dermatología':
				// Use dermatología options, fallback to empty if not available
				options = testLabelOptions.dermatología ?? [];
				break;
			default:
				options = testLabelOptions.enurmAdmin;
		}

		return options.map((exam: { id: string; value: string }) => ({
			id: exam.id,
			value: exam.id,
			name: exam.value
		}));
	}, [bankID]);

	// Enhanced loading skeleton component
	const LoadingSkeleton: React.FC = React.memo(() => (
		<div className='mt-8 space-y-6'>
			<div className='bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-100 p-6'>
				<div className='animate-pulse'>
					<div className='h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-1/4 mb-4' />
					<div className='h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-1/2 mb-6' />
					<div className='h-64 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg' />
				</div>
			</div>
		</div>
	));
	LoadingSkeleton.displayName = 'LoadingSkeleton';

	// Enhanced empty state component
	const EmptyState: React.FC = React.memo(() => (
		<div className='mt-8'>
			<div className='bg-gradient-to-br from-brand-50 via-white to-cyan-50 rounded-2xl shadow-xl border border-brand-100 p-12 text-center'>
				<div className='mx-auto h-32 w-32 text-brand-400 mb-6'>
					<div className='relative'>
						<ChartPieIcon className='h-32 w-32' />
						<SparklesIcon className='absolute -top-2 -right-2 h-8 w-8 text-yellow-400 animate-pulse' />
					</div>
				</div>
				<h3 className='text-xl font-bold text-gray-900 mb-2'>
					Ready to Analyze
				</h3>
				<p className='text-gray-600 max-w-md mx-auto mb-6'>
					Select an exam from the dropdown above to unlock detailed category
					breakdowns with beautiful visualizations.
				</p>
				<div className='inline-flex items-center text-sm text-brand-600 bg-brand-50 px-4 py-2 rounded-full'>
					<AcademicCapIcon className='h-4 w-4 mr-2' />
					25+ Exams Available
				</div>
			</div>
		</div>
	));
	EmptyState.displayName = 'EmptyState';

	// Custom tooltip for pie chart
	interface TooltipProps {
		active?: boolean;
		payload?: Array<{
			name: string;
			value: number;
		}>;
		label?: string;
	}

	const CustomTooltip: React.FC<TooltipProps> = ({ active, payload }) => {
		if (active === true && payload !== undefined && payload.length > 0) {
			const data = payload[0];
			const percentage = (
				(data.value / filteredQuestions.length) *
				100
			).toFixed(1);
			return (
				<div className='bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg p-4'>
					<p className='font-semibold text-gray-900'>{data.name}</p>
					<p className='text-brand-600 font-medium'>{data.value} questions</p>
					<p className='text-gray-600 text-sm'>{percentage}% of total</p>
				</div>
			);
		}
		return null;
	};

	return (
		<MainSection title='Category Breakdown'>
			<div className='my-8 w-full'>
				<div className='px-4 sm:px-6 lg:px-8'>
					{/* Enhanced Header with Gradient Background */}
					<div className='bg-gradient-to-r from-brand-600 via-brandDark-500 to-brand-500 rounded-2xl shadow-xl p-8 mb-8'>
						<div className='flex items-center justify-between'>
							<div>
								<h1 className='text-3xl font-bold text-white mt-0 mb-2'>
									Category Breakdown Analysis
								</h1>
								<p className='text-brand-100 text-lg'>
									Discover insights across question categories with interactive
									visualizations
								</p>
							</div>
							<div className='hidden md:block'>
								<ChartPieIcon className='h-16 w-16 text-white/80' />
							</div>
						</div>
					</div>

					{/* Enhanced Tabs Navigation */}
					<section id='tabs' className='mb-8' aria-label='Question Bank Tabs'>
						<div className='sm:hidden'>
							<label htmlFor='tabs-select' className='sr-only'>
								Select a question bank
							</label>
							<select
								id='tabs-select'
								name='tabs'
								className='block w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 bg-white/80 backdrop-blur-sm'
								onChange={(e) =>
									handleTabChange(e.target.value as QuestionBankID)
								}
								value={bankID}>
								{tabs.map((tab: Tab) => (
									<option key={tab.id} value={tab.id}>
										{tab.name}
									</option>
								))}
							</select>
						</div>

						<div className='hidden sm:block'>
							<div className='bg-white/60 backdrop-blur-sm rounded-2xl p-2 shadow-lg border border-white/20'>
								<nav className='flex space-x-2' aria-label='Tabs'>
									{tabs.map((tab: Tab) => (
										<button
											key={tab.id}
											className={classNames(
												tab.current
													? 'bg-gradient-to-r from-brandDark-500 to-brand-500 text-white shadow-lg'
													: 'text-gray-700 hover:text-brand-600 hover:bg-white/80',
												'rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 transform hover:scale-105'
											)}
											aria-current={tab.current ? 'page' : undefined}
											onClick={() => handleTabChange(tab.id)}>
											{tab.name}
										</button>
									))}
								</nav>
							</div>
						</div>
					</section>

					{/* Enhanced Controls Section */}
					<section className='mb-8'>
						<div className='bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-100 p-6'>
							<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
								<div className='flex-1'>
									<div className='flex items-center gap-3'>
										<ClipboardDocumentListIcon className='h-6 w-6 text-brand-600' />
										<div>
											<h2 className='text-lg font-semibold text-gray-900'>
												Exam Selection
											</h2>
											<p className='text-sm text-gray-600'>
												Choose an exam to analyze its question distribution
											</p>
										</div>
									</div>
								</div>

								<div className='flex gap-3'>
									<SelectElement
										defaultOption='-- Choose an Exam --'
										id='exam-selector'
										icon='quiz'
										iconFam='material-icons'
										isLabelHidden={true}
										isDefaultOptionDisabled={false}
										label='Choose an exam'
										options={examOptions}
										value={selectedExam}
										handleChange={handleExamChange}
										parentClass='min-w-[250px]'
									/>

									{showBreakdown && (
										<div className='flex bg-gray-100 rounded-lg p-1'>
											<button
												onClick={() => setViewMode('chart')}
												className={classNames(
													viewMode === 'chart'
														? 'bg-white shadow-sm text-brand-600'
														: 'text-gray-600',
													'p-2 rounded-md transition-all duration-200'
												)}>
												<ChartPieIcon className='h-5 w-5' />
											</button>
											<button
												onClick={() => setViewMode('table')}
												className={classNames(
													viewMode === 'table'
														? 'bg-white shadow-sm text-brand-600'
														: 'text-gray-600',
													'p-2 rounded-md transition-all duration-200'
												)}>
												<ChartBarIcon className='h-5 w-5' />
											</button>
										</div>
									)}
								</div>
							</div>
						</div>
					</section>

					{/* Alert Section */}
					{alertMessage !== null && alertMessage !== '' && (
						<Alert title='No Questions Found' type='warning' classes='mb-4'>
							{alertMessage}
						</Alert>
					)}

					{/* Loading State */}
					{loading && <LoadingSkeleton />}

					{/* Enhanced Results Section */}
					{showBreakdown && !loading && (
						<section id='breakdown-results' className='space-y-6'>
							{/* Stats Overview Cards */}
							<div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
								<div className='bg-gradient-to-br from-brandDark-500 to-brand-600 rounded-2xl p-6 text-white shadow-xl'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-brand-100 text-sm font-medium'>
												Total Questions
											</p>
											<p className='text-3xl font-bold'>
												{filteredQuestions.length}
											</p>
										</div>
										<ClipboardDocumentListIcon className='h-12 w-12 text-brand-200' />
									</div>
								</div>

								<div className='bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white shadow-xl'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-emerald-100 text-sm font-medium'>
												Categories
											</p>
											<p className='text-3xl font-bold'>
												{categoryData.length}
											</p>
										</div>
										<ChartPieIcon className='h-12 w-12 text-emerald-200' />
									</div>
								</div>

								<div className='bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-xl'>
									<div className='flex items-center justify-between'>
										<div>
											<p className='text-amber-100 text-sm font-medium'>Exam</p>
											<p className='text-lg font-bold truncate'>
												{selectedExam}
											</p>
										</div>
										<AcademicCapIcon className='h-12 w-12 text-amber-200' />
									</div>
								</div>
							</div>

							{/* Chart Section */}
							{viewMode === 'chart' && chartData.length > 0 && (
								<div className='bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 overflow-hidden'>
									<div className='bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200'>
										<h3 className='text-xl font-bold text-gray-900 flex items-center gap-2'>
											<ChartPieIcon className='h-6 w-6 text-brand-600' />
											Category Distribution
										</h3>
									</div>

									<div className='p-6'>
										<div className='w-full' style={{ height: '500px' }}>
											<ResponsiveContainer width='100%' height='100%'>
												<PieChart>
													<defs>
														{chartData.map((entry, index) => (
															<linearGradient
																key={`gradient-${index}`}
																id={`gradient-${index}`}
																x1='0%'
																y1='0%'
																x2='100%'
																y2='100%'>
																<stop
																	offset='0%'
																	stopColor={entry.color}
																	stopOpacity={0.8}
																/>
																<stop
																	offset='100%'
																	stopColor={entry.color}
																	stopOpacity={1}
																/>
															</linearGradient>
														))}
													</defs>
													<Pie
														data={chartData}
														cx='50%'
														cy='50%'
														outerRadius={140}
														innerRadius={60}
														paddingAngle={3}
														dataKey='value'
														animationBegin={0}
														animationDuration={1200}>
														{chartData.map((entry, index) => (
															<Cell
																key={`cell-${index}`}
																fill={`url(#gradient-${index})`}
																stroke='white'
																strokeWidth={2}
															/>
														))}
													</Pie>
													<Tooltip content={<CustomTooltip />} />
													<Legend
														verticalAlign='bottom'
														height={60}
														iconType='circle'
														wrapperStyle={{
															paddingTop: '20px',
															fontSize: '14px',
															fontWeight: '500'
														}}
													/>
												</PieChart>
											</ResponsiveContainer>
										</div>
									</div>
								</div>
							)}

							{/* Enhanced Table View */}
							{viewMode === 'table' && chartData.length > 0 && (
								<div className='bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 overflow-hidden'>
									<div className='bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200'>
										<h3 className='text-xl font-bold text-gray-900 flex items-center gap-2'>
											<ChartBarIcon className='h-6 w-6 text-brand-600' />
											Category Comparison
										</h3>
									</div>

									<div className='p-6'>
										<div className='w-full' style={{ height: '400px' }}>
											<ResponsiveContainer width='100%' height='100%'>
												<BarChart
													data={chartData}
													margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
													<defs>
														{chartData.map((entry, index) => (
															<linearGradient
																key={`bar-gradient-${index}`}
																id={`bar-gradient-${index}`}
																x1='0%'
																y1='0%'
																x2='0%'
																y2='100%'>
																<stop
																	offset='0%'
																	stopColor={entry.color}
																	stopOpacity={0.8}
																/>
																<stop
																	offset='100%'
																	stopColor={entry.color}
																	stopOpacity={1}
																/>
															</linearGradient>
														))}
													</defs>
													<CartesianGrid
														strokeDasharray='3 3'
														stroke='#f0f0f0'
													/>
													<XAxis
														dataKey='name'
														angle={-45}
														textAnchor='end'
														height={60}
														interval={0}
														fontSize={12}
													/>
													<YAxis />
													<Tooltip content={<CustomTooltip />} />
													<Bar
														dataKey='value'
														radius={[4, 4, 0, 0]}
														animationDuration={1000}>
														{chartData.map((entry, index) => (
															<Cell
																key={`bar-${index}`}
																fill={`url(#bar-gradient-${index})`}
															/>
														))}
													</Bar>
												</BarChart>
											</ResponsiveContainer>
										</div>
									</div>
								</div>
							)}

							{/* Category Summary Section */}
							<div className='bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 overflow-hidden'>
								<div className='bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200'>
									<h3 className='text-xl font-bold text-gray-900 flex items-center gap-2'>
										<ClipboardDocumentListIcon className='h-6 w-6 text-brand-600' />
										Category Summary
									</h3>
								</div>

								<div className='p-6'>
									{categoryData.length > 0 ? (
										<div className='space-y-3'>
											{categoryData.map((category: CategoryBreakdownData) => {
												const percentage = (
													(category.count / filteredQuestions.length) *
													100
												).toFixed(1);

												return (
													<div
														key={category.id}
														className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'>
														<div className='font-medium text-gray-900'>
															{category.name}
														</div>
														<div className='text-right'>
															<span className='font-semibold text-gray-900'>
																{category.count}
															</span>
															<span className='text-gray-500 ml-2'>
																({percentage}%)
															</span>
														</div>
													</div>
												);
											})}
										</div>
									) : (
										<div className='text-center py-8'>
											<p className='text-gray-500'>No categories to display</p>
										</div>
									)}
								</div>
							</div>

							{/* Enhanced Detailed Breakdown */}
							<div className='bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-100 overflow-hidden'>
								<div className='bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200'>
									<div className='flex items-center justify-between'>
										<h3 className='text-xl font-bold text-gray-900'>
											Detailed Category Breakdown
										</h3>

										{/* Expand/Collapse All Controls */}
										{categoryData.length > 0 && (
											<div className='flex gap-2'>
												<button
													onClick={expandAllCategories}
													className='text-sm text-brand-600 hover:text-brand-800 font-medium px-3 py-1 rounded-lg hover:bg-brand-50 transition-colors duration-200'>
													Expand All
												</button>
												<button
													onClick={collapseAllCategories}
													className='text-sm text-gray-600 hover:text-gray-800 font-medium px-3 py-1 rounded-lg hover:bg-gray-50 transition-colors duration-200'>
													Collapse All
												</button>
											</div>
										)}
									</div>
								</div>

								<div className='p-6'>
									{categoryData.length > 0 ? (
										<div className='space-y-6'>
											{categoryData.map(
												(
													category: CategoryBreakdownData,
													categoryIndex: number
												) => {
													const percentage = (
														(category.count / filteredQuestions.length) *
														100
													).toFixed(1);
													const gradientClass =
														GRADIENT_COLORS[
															categoryIndex % GRADIENT_COLORS.length
														];
													const isExpanded = expandedCategories.has(
														category.id
													);
													const hasSubcategories =
														category.subCategoryList.length > 0;

													return (
														<article key={category.id} className='group'>
															<div
																className={`bg-gradient-to-r ${gradientClass} rounded-2xl p-6 text-white shadow-lg transition-shadow duration-300 hover:shadow-xl`}>
																<header className='mb-4'>
																	<div className='flex items-center justify-between mb-3'>
																		<h4 className='text-2xl font-bold'>
																			{category.name}
																		</h4>
																		<div className='flex items-center gap-3'>
																			<span className='bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium'>
																				ID: {category.id}
																			</span>
																			<span className='bg-white/30 backdrop-blur-sm px-4 py-2 rounded-full text-lg font-bold'>
																				{category.count}
																			</span>
																		</div>
																	</div>

																	{/* Progress Bar */}
																	<div className='mb-4'>
																		<div className='flex justify-between text-sm mb-2'>
																			<span>Distribution</span>
																			<span>{percentage}% of total</span>
																		</div>
																		<div className='w-full bg-white/20 rounded-full h-3'>
																			<div
																				className='bg-white/80 h-3 rounded-full transition-all duration-1000 ease-out'
																				style={{
																					width: `${percentage}%`
																				}}
																			/>
																		</div>
																	</div>
																</header>

																{/* Collapsible Subcategories */}
																{hasSubcategories && (
																	<div className='bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden'>
																		{/* Subcategory Header with Toggle */}
																		<div
																			onClick={() =>
																				toggleCategoryExpansion(category.id)
																			}
																			className='w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors duration-200 cursor-pointer rounded-t-xl'
																			role='button'
																			tabIndex={0}
																			onKeyDown={(e) => {
																				if (
																					e.key === 'Enter' ||
																					e.key === ' '
																				) {
																					e.preventDefault();
																					toggleCategoryExpansion(category.id);
																				}
																			}}>
																			<div className='flex items-center gap-3'>
																				<h5 className='font-semibold text-left mt-0'>
																					Subcategories
																				</h5>
																				<span className='bg-white/20 px-2 py-1 rounded-full text-xs'>
																					{category.subCategoryList.length}
																				</span>
																			</div>

																			<div className='flex items-center gap-2'>
																				<span className='text-xs opacity-80'>
																					{isExpanded ? 'Hide' : 'Show'} Details
																				</span>
																				{isExpanded ? (
																					<ChevronDownIcon className='h-5 w-5 transform transition-transform duration-200' />
																				) : (
																					<ChevronRightIcon className='h-5 w-5 transform transition-transform duration-200' />
																				)}
																			</div>
																		</div>

																		{/* Expandable Content */}
																		<div
																			className={classNames(
																				'transition-all duration-300 ease-in-out',
																				isExpanded
																					? 'max-h-[2000px] opacity-100'
																					: 'max-h-0 opacity-0 overflow-hidden'
																			)}>
																			<div className='p-4 pt-0 space-y-3'>
																				{category.subCategoryList.map(
																					(
																						subCategory: SubCategoryBreakdown
																					) => {
																						const subPercentage = (
																							(subCategory.count /
																								category.count) *
																							100
																						).toFixed(1);

																						return (
																							<div
																								key={subCategory.name}
																								className='bg-white/10 rounded-lg p-3 transition-colors duration-200 hover:bg-white/20'>
																								<div className='flex justify-between items-start mb-2'>
																									<div className='flex-1'>
																										<h6 className='font-medium text-sm'>
																											{subCategory.name}
																										</h6>
																										{subCategory.specifications !==
																											undefined &&
																											subCategory.specifications
																												.length > 0 && (
																												<p className='text-xs opacity-80 mt-1 leading-relaxed'>
																													{
																														subCategory.specifications
																													}
																												</p>
																											)}
																									</div>
																									<div className='text-right ml-3 flex-shrink-0 flex items-center'>
																										<span className='font-bold text-lg mr-1'>
																											{subCategory.count}
																										</span>{' '}
																										-
																										<span className='text-xs opacity-80 ml-1'>
																											{subPercentage}%
																										</span>
																									</div>
																								</div>

																								{/* Mini progress bar */}
																								<div className='w-full bg-white/20 rounded-full h-1.5'>
																									<div
																										className='bg-white/60 h-1.5 rounded-full transition-all duration-700'
																										style={{
																											width: `${subPercentage}%`
																										}}
																									/>
																								</div>
																							</div>
																						);
																					}
																				)}
																			</div>
																		</div>
																	</div>
																)}
															</div>
														</article>
													);
												}
											)}
										</div>
									) : (
										<div className='text-center py-12'>
											<div className='mx-auto h-16 w-16 text-gray-400 mb-4'>
												<ChartPieIcon />
											</div>
											<p className='text-gray-500 text-lg'>
												No questions found for the selected exam.
											</p>
										</div>
									)}
								</div>
							</div>
						</section>
					)}

					{/* Enhanced Empty State */}
					{!showBreakdown && !loading && <EmptyState />}
				</div>
			</div>
		</MainSection>
	);
};

export default React.memo(CategoryBreakdown);
